import { useCallback } from 'react';
import { Channel } from '@weave/schema-gen-ts/dist/schemas/comm-preference/shared/v1/enums.pb';
import { MessageType_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import { ThreadType } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import { useGroupMessagingEnabled } from '@wetags/messaging-inbox';
import { AnimatePresence, motion } from 'motion/react';
import { useCheckSMSPreference } from '@frontend/api-comm-preference';
import { useInboxNavigate } from '@frontend/inbox-navigation';
import { CHAT_HEIGHTS, HEADER_HEIGHT, MiniChatBodyProps } from '@frontend/mini-chat-store';
import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { ThreadList, OptOutBanner, GroupThreadIssueBanner } from '@frontend/thread-body';
import { useGroupOptOutStatus, useGroupBlockedStatus } from '@frontend/thread-header';
import { InboxPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import { useDebouncedValue } from '@frontend/design-system';

export const MiniChatBody = ({
  groupId,
  threadId,
  groupThreadContext,
  threadType,
  focusThreadItemData,
  personPhone,
  unreadCount,
  isNew,
}: MiniChatBodyProps) => {
  const [groupMessagingEnabled] = useGroupMessagingEnabled();
  const isGroupThread =
    groupMessagingEnabled &&
    threadType === ThreadType.THREAD_TYPE_GROUP_MMS &&
    (groupThreadContext?.participants?.length ?? 0) > 1;

  const { data: smsPreference } = useCheckSMSPreference({
    userChannelAddress: formatPhoneNumberE164(personPhone),
    channel: Channel.CHANNEL_SMS,
    locationId: groupId,
    messageType: MessageType_Enum.MESSAGING_MANUAL,
  });
  const isOptedOut = !isGroupThread && smsPreference?.consented === false;
  // Debouncing this value allows virtuoso to finish handling the initial scroll before the banner is shown.
  // This avoids situations where the banner is shown, but the thread is scrolled up from the bottom a little.
  const debouncedOptOut = useDebouncedValue(isOptedOut, 500);

  // Group thread opt-out detection
  const groupParticipants = groupThreadContext?.participants ?? [];
  const { hasOptedOutParticipants, activeParticipants } = useGroupOptOutStatus({
    participants: groupParticipants,
    locationId: groupId,
    enabled: !!isGroupThread,
  });
  // Group thread blocked detection
  const { hasBlockedParticipants: groupHasBlocked, unblockedParticipants } = useGroupBlockedStatus({
    participants: groupParticipants,
    locationId: groupId,
    enabled: !!isGroupThread,
  });

  const groupOptedOut = !!isGroupThread && hasOptedOutParticipants;
  // Only show blocked banner if there are blocked-only participants (not also opted-out)
  const hasBlockedOnlyInMiniChat =
    groupHasBlocked &&
    groupParticipants.some(
      (p) =>
        p.phoneNumber &&
        unblockedParticipants.every((u) => u.phoneNumber !== p.phoneNumber) &&
        activeParticipants.some((a) => a.phoneNumber === p.phoneNumber)
    );
  const groupBlocked = !!isGroupThread && hasBlockedOnlyInMiniChat;

  const { navigateToThreadCreation } = useInboxNavigate();
  const handleStartNewThread = useCallback(() => {
    if (activeParticipants.length >= 2) {
      navigateToThreadCreation({
        locationId: groupId,
        state: { groupThreadContext: { participants: activeParticipants } },
      });
    } else if (activeParticipants.length === 1) {
      navigateToThreadCreation({
        personPhone: activeParticipants[0]?.phoneNumber,
        locationId: groupId,
      });
    }
  }, [navigateToThreadCreation, activeParticipants, groupId]);

  return (
    <motion.div
      initial={{
        opacity: 1,
      }}
      animate={{
        opacity: 1,
      }}
      css={{
        position: 'relative',
        maxHeight: CHAT_HEIGHTS['full'] - HEADER_HEIGHT,
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
      exit={{
        opacity: 0,
      }}
    >
      <ThreadList
        threadId={threadId}
        groupId={groupId}
        focusThreadItemData={focusThreadItemData}
        personPhone={personPhone}
        unreadCount={unreadCount}
        uiType='mini-chat'
        hiddenContextActions={['tag', 'delete', 'copy', 'tasks']}
        hideWritebacks
        trackingIdPrefix={InboxPrefixes.Popout}
        isNew={isNew}
        hasGroupThreadIssue={!!isGroupThread && (groupOptedOut || groupBlocked)}
      />
      <AnimatePresence>
        {debouncedOptOut && (
          <>
            <motion.div
              key='opt-out-overlay'
              initial={{
                opacity: 0,
              }}
              animate={{
                opacity: 1,
              }}
              exit={{
                opacity: 0,
              }}
              css={{
                position: 'absolute',
                height: '100%',
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: `${theme.colors.white}7F`,
                zIndex: theme.zIndex.middle,
              }}
            />
            <motion.div
              key='opt-out-banner-wrapper'
              initial={{
                height: 0,
              }}
              animate={{
                height: 'auto',
              }}
              exit={{
                height: 0,
              }}
              css={{
                padding: theme.spacing(1),
                borderTop: `1px solid ${theme.colors.neutral20}`,
                width: '100%',
              }}
            >
              <OptOutBanner
                textProps={{
                  size: 'medium',
                }}
                css={{
                  gap: theme.spacing(1),
                }}
              />
            </motion.div>
          </>
        )}
      </AnimatePresence>
      {groupOptedOut && (
        <div
          css={{
            padding: theme.spacing(1),
            borderTop: `1px solid ${theme.colors.neutral20}`,
            width: '100%',
          }}
        >
          <GroupThreadIssueBanner
            variant='opted-out'
            compact
            disableNewThread={activeParticipants.length === 0}
            onStartNewThread={handleStartNewThread}
          />
        </div>
      )}
      {groupBlocked && (
        <div
          css={{
            padding: theme.spacing(1),
            borderTop: `1px solid ${theme.colors.neutral20}`,
            width: '100%',
          }}
        >
          <GroupThreadIssueBanner
            variant='blocked'
            compact
            disableNewThread={unblockedParticipants.length === 0}
            onStartNewThread={handleStartNewThread}
          />
        </div>
      )}
    </motion.div>
  );
};
